import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';
import { AXIS_IDS, COND_IDS, TOTAL_QUESTIONS } from './questions.js';
import { LOCALES } from './content/index.js';
import { TIERS } from './pricing.js';
extendZodWithOpenApi(z);
/** Одна и та же схема валидирует запрос, описывает эндпоинт и типизирует клиент. */
export const LocaleSchema = z.enum(LOCALES).openapi('Locale');
export const SexSchema = z.enum(['M', 'F']).openapi('Sex');
export const RoleSchema = z.enum(['USER', 'MATCHMAKER', 'ADMIN']).openapi('Role');
export const AnswerSchema = z.union([z.number().int(), z.string().max(60), z.null()]);
export const AnswersSchema = z
    .array(AnswerSchema)
    .length(TOTAL_QUESTIONS)
    .openapi('Answers', { description: `Ровно ${TOTAL_QUESTIONS} ответов в порядке QUESTIONS` });
export const AxisScoresSchema = z
    .object(Object.fromEntries(AXIS_IDS.map((id) => [id, z.number().int().min(0).max(100)])))
    .openapi('AxisScores');
export const UserSchema = z
    .object({
    id: z.string().uuid(),
    /**
     * Почта есть у тех, кто вошёл через Google. У зарегистрировавшегося по
     * номеру её нет вовсе — и выдумывать её ради непустого поля значило бы
     * показывать человеку адрес, которого он не заводил.
     */
    email: z.string().email().nullable(),
    /** Телефон в E.164. Заполнен у тех, кто регистрировался по номеру. */
    phone: z.string().nullable(),
    name: z.string(),
    sex: SexSchema.nullable(),
    role: RoleSchema,
    locale: LocaleSchema,
    contact: z.string().nullable(),
    optInMatching: z.boolean(),
    /**
     * Выключатель пушей. Отдельно от системного разрешения: то спрашивает
     * телефон и живёт в его настройках, а это — наш переключатель в профиле,
     * и без него человеку пришлось бы выбирать между «все уведомления» и
     * «никаких» на уровне ОС.
     */
    pushEnabled: z.boolean(),
    hasProfile: z.boolean(),
    /** Дал ли человек согласие на обработку персональных данных. */
    hasConsent: z.boolean(),
})
    .openapi('User');
/**
 * Токен Expo. Проверяется формой, а не только длиной: чужая строка в этой
 * колонке заняла бы уникальный ключ и молча отправляла бы пуши в никуда.
 * Обе формы — ExponentPushToken[...] и ExpoPushToken[...] — живые, SDK выдаёт
 * то одну, то другую в зависимости от версии.
 */
export const ExpoPushTokenSchema = z
    .string()
    .regex(/^Expo(nent)?PushToken\[[^\s[\]]+\]$/, 'Это не похоже на токен Expo')
    .max(200);
export const RegisterDeviceSchema = z
    .object({
    expoToken: ExpoPushTokenSchema,
    platform: z.enum(['IOS', 'ANDROID']),
    /**
     * Язык этого телефона. Необязателен: если приложение его не прислало,
     * берётся язык профиля — пуш на непонятном языке хуже пуша на языке сайта.
     */
    locale: LocaleSchema.optional(),
})
    .openapi('RegisterDevice');
/** Токен наружу не возвращается: клиент его и так знает, а в логах он лишний. */
export const DeviceSchema = z
    .object({
    id: z.string().uuid(),
    platform: z.enum(['IOS', 'ANDROID']),
    locale: LocaleSchema,
    createdAt: z.string(),
    lastSeenAt: z.string(),
})
    .openapi('Device');
/** Текущая редакция согласия; при изменении текста версия поднимается. */
export const CONSENT_VERSION = '2026-08-1';
export const ConsentSchema = z
    .object({ version: z.string().min(1).default(CONSENT_VERSION) })
    .openapi('Consent');
export const PublicConfigSchema = z
    .object({
    googleEnabled: z.boolean(),
    /**
     * Настроен ли вход из мобильного приложения. Отдельно от googleEnabled:
     * тот отвечает за редирект в браузере и живёт на веб-ключах, а приложению
     * нужны клиенты iOS и Android, которые заводятся позже и отдельно. Одного
     * флага на оба входа не хватает — кнопка в приложении обещала бы вход там,
     * где сервер ответит google_not_configured.
     */
    nativeAuthEnabled: z.boolean(),
    /**
     * Включён ли вход по логину и паролю. Он существует ради одной учётки —
     * ревьюера Apple (правило 4.8), — и на стенде без её переменных маршрута
     * нет вовсе. Флаг нужен клиенту затем же, зачем и остальные: показать
     * форму, в которую сервер ответит 404, хуже, чем не показать её вовсе.
     */
    passwordLoginEnabled: z.boolean(),
    consentVersion: z.string(),
    /** Настроен ли Kaspi на этом стенде: без него кнопку оплаты показывать нечестно. */
    paymentsEnabled: z.boolean(),
    /**
     * Настроена ли покупка внутри приложения (RevenueCat). Отдельный флаг, а не
     * второе имя для `paymentsEnabled`: это разные каналы с разными ключами и
     * разными правилами. Смешать их — значит однажды показать в вебе кнопку
     * стора, а в приложении ссылку на Kaspi, то есть ровно то, за что снимают
     * с ревью.
     */
    iapEnabled: z.boolean(),
    tiers: z.array(z.object({ tier: z.enum(TIERS), price: z.number().int() })),
})
    .openapi('PublicConfig');
export const UpdateMeSchema = z
    .object({
    /**
     * Имя приходит из Google, а видит его будущий супруг в отчёте пары — там
     * латиница и никнеймы смотрятся дико, поэтому имя разрешено поправить.
     * Границы те же, что у имени в приглашении: это одна и та же величина, и
     * третья цифра здесь означала бы, что приглашение и профиль расходятся.
     * Пустое имя не принимается ни в каком виде: `''` в отчёте выглядит
     * поломкой, а не выбором, — потому `trim().min(1)`, а не `nullable()`.
     */
    name: z.string().trim().min(1).max(80).optional(),
    sex: SexSchema.optional(),
    locale: LocaleSchema.optional(),
    contact: z.string().max(60).nullable().optional(),
    optInMatching: z.boolean().optional(),
    /**
     * Здесь же, а не отдельным маршрутом: для человека это такая же строка
     * настроек профиля, как язык, и второй эндпоинт ради одного булева поля
     * означал бы вторую обработку ошибок на клиенте.
     */
    pushEnabled: z.boolean().optional(),
})
    .openapi('UpdateMe');
export const AssessmentSchema = z
    .object({
    id: z.string().uuid(),
    version: z.number().int(),
    status: z.enum(['DRAFT', 'COMPLETE']),
    answers: AnswersSchema,
    answered: z.number().int(),
    total: z.number().int(),
    completedAt: z.string().datetime().nullable(),
})
    .openapi('Assessment');
export const PatchAssessmentSchema = z
    .object({
    answers: z.array(z.object({ index: z.number().int().min(0), value: AnswerSchema })).min(1),
})
    .openapi('PatchAssessment');
export const ProfileSchema = z
    .object({
    axes: AxisScoresSchema,
    age: z.number().int().nullable(),
    city: z.string(),
    faith: z.number().int().nullable(),
    lang: z.number().int().nullable(),
    edu: z.number().int().nullable(),
    past: z.number().int().nullable(),
    field: z.number().int().nullable(),
    condLevels: z.array(z.number().int()).length(COND_IDS.length),
    forkAnswers: z.array(z.number().int()),
    stems: z.array(z.string()),
})
    .openapi('Profile');
export const ZoneSchema = z
    .object({
    code: z.string(),
    tier: z.enum(['red', 'yellow', 'green']),
    params: z.record(z.union([z.string(), z.number()])).optional(),
    talk: z.array(z.string()).optional(),
})
    .openapi('Zone', { description: 'Код и параметры; текст подставляет клиент по локали' });
export const PairReportSchema = z
    .object({
    pairId: z.string().uuid(),
    a: z.object({ name: z.string(), profile: ProfileSchema }),
    b: z.object({ name: z.string(), profile: ProfileSchema }),
    zones: z.array(ZoneSchema),
    red: z.number().int(),
    yellow: z.number().int(),
    green: z.number().int(),
})
    .openapi('PairReport');
export const TierSchema = z.enum(TIERS).openapi('Tier');
export const TeaserZoneSchema = z
    .object({
    tier: z.enum(['red', 'yellow', 'green']),
    subject: z.object({ kind: z.enum(['cond', 'axis', 'fork']), id: z.string() }).nullable(),
    locked: z.boolean(),
    code: z.string().optional(),
    params: z.record(z.union([z.string(), z.number()])).optional(),
})
    .openapi('TeaserZone', { description: 'Закрытая зона приходит без формулировки' });
export const PairTeaserSchema = z
    .object({
    zones: z.array(TeaserZoneSchema),
    red: z.number().int(),
    yellow: z.number().int(),
    green: z.number().int(),
    locked: z.number().int(),
})
    .openapi('PairTeaser');
export const OfferSchema = z
    .object({
    tiers: z.array(z.object({ tier: TierSchema, price: z.number().int() })),
    currency: z.literal('KZT'),
})
    .openapi('Offer', { description: 'Тарифы, доступные к покупке прямо сейчас' });
/**
 * Одна витрина на оба состояния: до оплаты приходит teaser и предложение,
 * после — полный отчёт. Клиенту не нужно гадать, какой из ответов он получил.
 */
export const PairViewSchema = z
    .object({
    pairId: z.string().uuid(),
    a: z.object({ name: z.string() }),
    b: z.object({ name: z.string() }),
    access: z.enum(['teaser', 'full']),
    red: z.number().int(),
    yellow: z.number().int(),
    green: z.number().int(),
    teaser: PairTeaserSchema.nullable(),
    report: PairReportSchema.nullable(),
    offer: OfferSchema.nullable(),
    /**
     * Второй участник купил отчёт, а зритель — нет. Доступа это не даёт:
     * покупка личная. Нужно, чтобы предложить попросить у пары готовый PDF
     * вместо второй оплаты того же разбора.
     */
    partnerPaid: z.boolean(),
    /** Имя второго участника — им подписаны и просьба, и кнопка «Поделиться». */
    partnerName: z.string(),
    /**
     * Своя пометка зрителя о втором участнике: `recipientName`, если смотрит
     * автор, `senderName`, если принявший. Чужую здесь не увидеть.
     *
     * Отдельно от `partnerName`, а не вместо него: настоящее имя нужно и для
     * PDF, и для того, чтобы человек понимал, кого он подписал по-своему.
     * Подставлять пометку вместо имени — значит однажды показать её самому
     * названному в его же отчёте.
     */
    partnerNote: z.string().nullable(),
    /**
     * Кто из двоих смотрит: `a` — автор приглашения, `b` — принявший.
     *
     * Обязательное и приходит в обоих состояниях витрины, тизер не исключение:
     * карта осей рисуется и до оплаты. Без него зрителя вычисляли сравнением
     * имён, а у двух тёзок в паре «Вы» и партнёр менялись местами на всей
     * карте — молча и ровно там, ради чего экран и существует.
     */
    viewerIs: z.enum(['a', 'b']),
})
    .openapi('PairView');
/**
 * `REFUNDED` — деньги вернулись покупателю, доступ снят. Отдельно от `FAILED`:
 * та оплата не состоялась вовсе, а эта состоялась и была отменена. Клиенту
 * разница видна на экране — «оплата не прошла» человеку, у которого деньги
 * забрали и вернули, звучит как поломка.
 */
export const PaymentStatusSchema = z
    .enum(['PENDING', 'PAID', 'FAILED', 'EXPIRED', 'REFUNDED'])
    .openapi('PaymentStatus');
/**
 * Канал, по которому уходят деньги. Kaspi — сайт, где комиссии нет; APPLE и
 * GOOGLE — приложение, где правила сторов не оставляют другого способа продать
 * то, что читают внутри приложения.
 */
export const PaymentProviderSchema = z
    .enum(['KASPI', 'APPLE', 'GOOGLE'])
    .openapi('PaymentProvider');
export const CreatePaymentSchema = z
    .object({
    pairId: z.string().uuid(),
    tier: TierSchema,
    /**
     * Дефолт KASPI: веб про сторы не знает и шлёт тело без этого поля. Не
     * определять канал по клиенту (`AuthClient`) намеренно — тогда контракт
     * читался бы только вместе с кодом входа, а телу запроса нечем было бы
     * возразить.
     */
    provider: PaymentProviderSchema.default('KASPI'),
})
    .openapi('CreatePayment');
export const PaymentSchema = z
    .object({
    id: z.string().uuid(),
    pairId: z.string().uuid(),
    tier: TierSchema,
    amount: z.number().int(),
    status: PaymentStatusSchema,
    provider: PaymentProviderSchema,
    /** Ссылка на оплату в Kaspi; после конечного статуса уже неактуальна. */
    paymentLink: z.string().nullable(),
    expiresAt: z.string().datetime().nullable(),
    /**
     * Как часто опрашивать статус — интервал приходит от Kaspi. У покупки в
     * сторе опроса нет вовсе: статус меняют `confirm` и вебхук, — поэтому
     * здесь null, а не выдуманное число. Число означало бы «опрашивай», и
     * клиент честно опрашивал бы вечно.
     */
    pollIntervalMs: z.number().int().nullable(),
})
    .openapi('Payment');
/** Имя получателя — приватная пометка автора ссылки, наружу она не уходит. */
export const CreateInviteSchema = z
    .object({ recipientName: z.string().trim().min(1).max(80) })
    .openapi('CreateInvite');
/**
 * Как я называю для себя второго участника пары.
 *
 * Поле одно, а колонки две: автор ссылки правит `recipientName`, принявший —
 * `senderName`. Кто есть кто, сервер знает и без подсказки клиента, а два
 * почти одинаковых маршрута разошлись бы в проверках.
 *
 * `null` — стереть пометку. Пустой строки здесь нет намеренно: у пары без
 * пометки её действительно нет, и `''` в истории врало бы.
 */
export const PairNoteSchema = z
    .object({ name: z.string().trim().min(1).max(80).nullable() })
    .openapi('PairNote');
/** Пометки этой пары глазами зрителя: чужая всегда null. */
export const PairNotesSchema = z
    .object({
    recipientName: z.string().nullable(),
    senderName: z.string().nullable(),
})
    .openapi('PairNotes');
export const InviteSchema = z
    .object({
    id: z.string().uuid(),
    token: z.string(),
    url: z.string(),
    recipientName: z.string(),
    expiresAt: z.string().datetime(),
})
    .openapi('Invite');
export const InviteStatusSchema = z
    .enum(['PENDING', 'ACCEPTED', 'EXPIRED', 'REVOKED'])
    .openapi('InviteStatus');
/**
 * Строка истории «кому я отдал ссылку». Анкетных данных тут нет — только имя
 * и факт завершения теста: сам отчёт остаётся за платной витриной пары.
 */
export const InviteHistoryItemSchema = z
    .object({
    id: z.string().uuid(),
    /**
     * Роль зрителя в этой паре: `owner` — он выдал ссылку, `partner` — принял
     * чужую. История показывает обе стороны, но видят они разное.
     */
    role: z.enum(['owner', 'partner']),
    /** Только у автора: ссылку-приглашение принявшему больше не показываем. */
    token: z.string().nullable(),
    url: z.string().nullable(),
    /** Приватная пометка автора; у принявшего всегда null. */
    recipientName: z.string().nullable(),
    /**
     * Зеркальная пометка принявшего — как он называет для себя автора ссылки.
     * У автора всегда null: это заметка для себя, а не переименование человека,
     * и показывать её тому, кого так записали, нельзя.
     */
    senderName: z.string().nullable(),
    status: InviteStatusSchema,
    /** Второй участник пары с точки зрения зрителя. */
    partner: z.object({ name: z.string() }).nullable(),
    /** Принять ссылку и пройти тест — разные события. */
    partnerCompletedTest: z.boolean(),
    /** Тест завершили оба: только тогда отчёт по паре вообще собирается. */
    reportReady: z.boolean(),
    /** Полный разбор открыт лично зрителю — можно читать и скачивать PDF. */
    hasFullAccess: z.boolean(),
    /** Отчёт купил второй участник. Доступа не даёт: покупка личная. */
    partnerPaid: z.boolean(),
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
})
    .openapi('InviteHistoryItem');
export const MatchSchema = z
    .object({
    id: z.string().uuid(),
    candidate: z.object({
        id: z.string().uuid(),
        name: z.string(),
        age: z.number().int().nullable(),
        city: z.string(),
        /** Контакт отдаётся только после одобрения свахой и согласия обеих сторон. */
        contact: z.string().nullable(),
    }),
    score: z.number().int(),
    tier: z.enum(['introduce', 'possible', 'neutral', 'rejected']),
    parts: z.object({
        home: z.number(),
        kin: z.number(),
        conditions: z.number(),
        role: z.number(),
        forks: z.number(),
        pace: z.number(),
    }),
    roleModel: z.object({ id: z.string(), score: z.number() }),
    reasons: z.array(z.object({
        code: z.string(),
        tone: z.enum(['plus', 'minus']),
        params: z.record(z.union([z.string(), z.number()])).optional(),
    })),
    flags: z.array(z.object({ code: z.string(), subjectId: z.string().nullable(), hard: z.boolean() })),
    status: z.enum(['NEW', 'APPROVED', 'REJECTED', 'SENT']),
})
    .openapi('Match');
export const AdminMatchDecisionSchema = z
    .object({ status: z.enum(['APPROVED', 'REJECTED', 'SENT']) })
    .openapi('AdminMatchDecision');
export const AdminGrantRequestSchema = z
    .object({
    /** Ссылка на пару, путь, id или токен приглашения — что админ скопировал, то и принимаем. */
    ref: z.string().min(1),
    note: z.string().max(500).optional(),
})
    .openapi('AdminGrantRequest');
/**
 * Чем человек опознаётся в админке.
 *
 * Пришедший через Google опознаётся почтой, зарегистрировавшийся по телефону —
 * номером, и обе колонки необязательны по отдельности. Пустыми одновременно
 * они не бывают — войти без единого логина не через что, — но выразить это
 * типом нельзя, поэтому обнуляемы оба, а клиент показывает то, что есть.
 */
const adminLoginFields = {
    email: z.string().nullable(),
    phone: z.string().nullable(),
};
export const AdminGrantSchema = z
    .object({
    pairId: z.string().uuid(),
    tier: z.enum(['FULL', 'PRO']),
    note: z.string().nullable(),
    grantedAt: z.string(),
    /** null — доступ открыт. */
    revokedAt: z.string().nullable(),
    grantedByName: z.string(),
    /** Пару не приняли — отчёт не соберётся, даже если доступ открыт. */
    pairReady: z.boolean(),
    hasPayment: z.boolean(),
    reportUrl: z.string(),
    a: z.object({ name: z.string(), ...adminLoginFields }),
    b: z.object({ name: z.string(), ...adminLoginFields }).nullable(),
})
    .openapi('AdminGrant');
/**
 * Строка журнала действий. Сервер отдаёт код действия, а не готовую фразу:
 * подпись собирает клиент по локали, как и везде в этом API.
 */
export const AdminAuditEntrySchema = z
    .object({
    id: z.string().uuid(),
    at: z.string(),
    action: z.string(),
    /** null — автора не осталось: аккаунт удалён. */
    actor: z.object({ id: z.string(), name: z.string(), ...adminLoginFields }).nullable(),
    subjectId: z.string().nullable(),
    meta: z.record(z.unknown()).nullable(),
    /** Заполнено, когда subjectId оказался парой: с кем что сделали и по какой ссылке. */
    pair: z
        .object({
        id: z.string(),
        url: z.string(),
        a: z.string(),
        b: z.string().nullable(),
    })
        .nullable(),
    /** Заполнено, когда subjectId оказался человеком: просмотр анкеты, решение по паре. */
    subjectUser: z.object({ id: z.string(), name: z.string(), ...adminLoginFields }).nullable(),
})
    .openapi('AdminAuditEntry');
export const AdminAuditPageSchema = z
    .object({
    items: z.array(AdminAuditEntrySchema),
    /** id последней отданной строки; null — журнал закончился. */
    nextCursor: z.string().nullable(),
})
    .openapi('AdminAuditPage');
/**
 * Воронка продукта: от регистрации до купленного отчёта.
 *
 * Считается по базе и журналу действий, а не по Amplitude. Так цифры сходятся
 * с тем, что лежит в таблицах, и доступны без ключа стороннего сервиса —
 * админку открывают, чтобы свериться с реальностью, а не с моделью события.
 * Обратная сторона: шаги, которых в базе нет, здесь не появятся. Их ровно
 * один — «ссылку скопировали» (`invite_link_copied`, `invite_shared`): нажатие
 * кнопки происходит целиком в браузере и телефоне, и сервер о нём не узнаёт.
 * Ближайшая к нему ступень в базе — `invitesCreated`, приглашение заведено.
 *
 * Числа — это люди или пары, а не события; исключение оговорено у поля.
 */
export const AdminFunnelSchema = z
    .object({
    /** Начало окна, ISO. null — считали за всё время. */
    from: z.string().nullable(),
    /** Конец окна, ISO: момент запроса. */
    to: z.string(),
    /** Аккаунтов заведено. */
    registered: z.number().int(),
    /** Людей, у которых появилась хоть одна анкета. */
    testStarted: z.number().int(),
    /**
     * Начали и не закончили: `testStarted − testCompleted` в том же окне.
     * Считается вычитанием, а не отдельным запросом за черновиками: человек
     * может завести черновик второй версии поверх пройденной первой, и запрос
     * «есть DRAFT» посчитал бы его бросившим.
     */
    testAbandoned: z.number().int(),
    /** Людей, дошедших до конца анкеты. */
    testCompleted: z.number().int(),
    /** Приглашений заведено. Ближайшее к «ссылку скопировали», см. выше. */
    invitesCreated: z.number().int(),
    /** Приглашений принято: второй человек перешёл по ссылке и подтвердил. */
    invitesAccepted: z.number().int(),
    /** Пар, где анкеты прошли оба, — им открыт бесплатный разбор. */
    pairsBothDone: z.number().int(),
    /** Скачиваний PDF — событий, а не людей: один отчёт качают и по три раза. */
    pdfDownloads: z.number().int(),
    /** Людей, скачавших хотя бы один PDF. */
    pdfDownloaders: z.number().int(),
    /** Пар с оплаченным полным отчётом: касса Kaspi и покупки в сторах. */
    paidPairs: z.number().int(),
    /**
     * Пар с доступом, открытым руками из админки. Это второй канал продажи —
     * перевод мимо кассы, — и складывать его с `paidPairs` нельзя: у пары
     * может быть и оплата, и грант.
     */
    grantedPairs: z.number().int(),
})
    .openapi('AdminFunnel');
export const ErrorSchema = z
    .object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
})
    .openapi('Error');
export { z };
//# sourceMappingURL=schemas.js.map